import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { getAccountingSettings, updateAccountingSettings } from "@/lib/accounting/accounting.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/accounting/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const getFn = useServerFn(getAccountingSettings);
  const setFn = useServerFn(updateAccountingSettings);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["accounting", "settings"], queryFn: () => getFn() });
  const [s, setS] = useState<any>(null);
  useEffect(() => { if (data) setS(data); }, [data]);

  if (isLoading || !s) return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;

  const save = async () => {
    try {
      await setFn({ data: {
        invoice_prefix: s.invoice_prefix,
        next_invoice_number: Number(s.next_invoice_number),
        default_payment_terms_days: Number(s.default_payment_terms_days),
        default_tax_rate: Number(s.default_tax_rate),
        default_deposit_percent: Number(s.default_deposit_percent),
        payment_instructions: s.payment_instructions,
        accepted_payment_methods: typeof s.accepted_payment_methods === "string" ? s.accepted_payment_methods.split(",").map((x: string) => x.trim()) : s.accepted_payment_methods,
        reminder_days: typeof s.reminder_days === "string" ? s.reminder_days.split(",").map((x: string) => Number(x.trim())) : s.reminder_days,
        late_notice_template: s.late_notice_template,
        export_format: s.export_format,
      } });
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["accounting", "settings"] });
    } catch (e) { toast.error((e as Error).message); }
  };

  const F = (label: string, key: string, type: string = "text", help?: string) => (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold block mb-1">{label}</span>
      <input type={type} value={s[key] ?? ""} onChange={(e) => setS({ ...s, [key]: e.target.value })} className={inp} />
      {help && <span className="text-[10px] text-zinc-500 mt-1 block">{help}</span>}
    </label>
  );

  return (
    <div className="space-y-5 max-w-3xl">
      <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 grid md:grid-cols-2 gap-4">
        {F("Invoice prefix", "invoice_prefix", "text", "e.g. MP-")}
        {F("Next invoice number", "next_invoice_number", "number")}
        {F("Default payment terms (days)", "default_payment_terms_days", "number")}
        {F("Default tax rate", "default_tax_rate", "number", "Decimal, e.g. 0.055 for 5.5%")}
        {F("Default deposit %", "default_deposit_percent", "number")}
        <label className="block md:col-span-2">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold block mb-1">Accepted payment methods (comma-separated)</span>
          <input value={Array.isArray(s.accepted_payment_methods) ? s.accepted_payment_methods.join(", ") : s.accepted_payment_methods ?? ""} onChange={(e) => setS({ ...s, accepted_payment_methods: e.target.value })} className={inp} />
        </label>
        <label className="block md:col-span-2">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold block mb-1">Reminder days (comma-separated, e.g. 3,7,14)</span>
          <input value={Array.isArray(s.reminder_days) ? s.reminder_days.join(", ") : s.reminder_days ?? ""} onChange={(e) => setS({ ...s, reminder_days: e.target.value })} className={inp} />
        </label>
        <label className="block md:col-span-2">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold block mb-1">Payment instructions</span>
          <textarea value={s.payment_instructions ?? ""} onChange={(e) => setS({ ...s, payment_instructions: e.target.value })} rows={3} className={inp} />
        </label>
        <label className="block md:col-span-2">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold block mb-1">Late notice template</span>
          <textarea value={s.late_notice_template ?? ""} onChange={(e) => setS({ ...s, late_notice_template: e.target.value })} rows={3} className={inp} />
        </label>
      </section>
      <div className="flex justify-end">
        <button onClick={save} className="text-xs px-5 py-2 rounded-full bg-amber-300 text-zinc-950 font-semibold">Save settings</button>
      </div>
    </div>
  );
}

const inp = "w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-amber-300/40";